<script setup>
/**
 * @fileoverview Color-wheel glaze picker. Every glaze is a dot on a hue wheel
 * (angle = hue, light toward the center, dark toward the rim); neutrals sit in
 * the gray core. Hovering shows a magnifying lens that zooms in on the area
 * under the cursor, rings the glaze nearest the center and names it in large
 * type; clicking picks that glaze.
 *
 * Touch: press and drag, the lens floats above the finger, lift to pick.
 * Keyboard: the wheel is a listbox — arrow keys move through glazes (neutrals,
 * then clockwise around the ring), Enter/Space picks.
 *
 * @component
 */
import { ref, shallowRef, computed, nextTick } from 'vue';
import {
    layoutWheel,
    nearestDot,
    RING_BACKGROUND,
    CORE_BACKGROUND,
    CORE_RADIUS,
} from '../utils/colorWheel.js';

const props = defineProps({
    /** Glaze name → hex */
    colors: { type: Object, required: true },
    selectedName: { type: String, default: null },
});

const emit = defineEmits(['select']);

const ZOOM = 4;
const LENS_SIZE = 200;
const LENS_RADIUS = LENS_SIZE / 2;
/** Field-space radius (px) in which a glaze can be targeted: the lens's footprint */
const PICK_RADIUS = LENS_RADIUS / ZOOM;
/** On touch the lens floats this far above the finger so it stays visible */
const TOUCH_LIFT = 120;

const uid = Math.random().toString(36).slice(2, 8);
const optionId = (i) => `cm-wheel-${uid}-${i}`;
const corePct = `${CORE_RADIUS * 100}%`;
const coreOffset = `${50 - CORE_RADIUS * 50}%`;

const fieldEl = ref(null);
const size = ref(0);
const lens = ref(null); // { x, y, lift } in field px
/** shallowRef keeps the raw dot object so `d === hoverDot` identity checks work */
const hoverDot = shallowRef(null);
const activeIndex = ref(-1);
let pointerDown = false;

const dots = computed(() => layoutWheel(props.colors));

const activeDescendant = computed(() =>
    activeIndex.value >= 0 && activeIndex.value < dots.value.length
        ? optionId(activeIndex.value)
        : undefined,
);

function measure() {
    const rect = fieldEl.value?.getBoundingClientRect();
    if (rect) size.value = rect.width;
    return rect;
}

function updateFromPointer(e) {
    const rect = measure();
    if (!rect || !rect.width) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    lens.value = { x, y, lift: e.pointerType === 'touch' ? TOUCH_LIFT : 0 };
    hoverDot.value = nearestDot(dots.value, x, y, rect.width, PICK_RADIUS);
}

function onPointerMove(e) {
    if (e.pointerType === 'touch' && !pointerDown) return;
    updateFromPointer(e);
}

function onPointerDown(e) {
    pointerDown = true;
    if (e.pointerType === 'touch') {
        fieldEl.value?.setPointerCapture?.(e.pointerId);
        updateFromPointer(e);
    }
}

function onPointerUp(e) {
    if (!pointerDown) return;
    pointerDown = false;
    updateFromPointer(e);
    if (hoverDot.value) {
        activeIndex.value = dots.value.indexOf(hoverDot.value);
        emit('select', hoverDot.value.name);
    }
    if (e.pointerType === 'touch') hideLens();
}

function hideLens() {
    pointerDown = false;
    lens.value = null;
    hoverDot.value = null;
}

function onPointerLeave(e) {
    if (e.pointerType !== 'touch') hideLens();
}

/** Keyboard: show the lens on the active glaze. */
function showLensAt(index) {
    const d = dots.value[index];
    if (!d) return;
    measure();
    lens.value = { x: d.x * size.value, y: d.y * size.value, lift: 0 };
    hoverDot.value = d;
}

function onFocus() {
    if (activeIndex.value < 0) {
        const selected = dots.value.findIndex((d) => d.name === props.selectedName);
        activeIndex.value = selected >= 0 ? selected : 0;
    }
    // A click also focuses the wheel; only keyboard focus should move the lens
    if (!pointerDown) nextTick(() => showLensAt(activeIndex.value));
}

function onKeydown(e) {
    const count = dots.value.length;
    if (!count) return;
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (step) {
        e.preventDefault();
        activeIndex.value = (activeIndex.value + step + count) % count;
        showLensAt(activeIndex.value);
    } else if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        activeIndex.value = e.key === 'Home' ? 0 : count - 1;
        showLensAt(activeIndex.value);
    } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        const d = dots.value[activeIndex.value];
        if (d) emit('select', d.name);
    } else if (e.key === 'Escape') {
        hideLens();
    }
}

const lensStyle = computed(() => {
    if (!lens.value) return {};
    // Keep the lens window inside the field so the sidebar never clips it; the
    // zoomed content still centers on the pointer (lensContentStyle).
    const clamp = (v) => Math.min(Math.max(v, 0), Math.max(size.value - LENS_SIZE, 0));
    return {
        width: `${LENS_SIZE}px`,
        height: `${LENS_SIZE}px`,
        left: `${clamp(lens.value.x - LENS_RADIUS)}px`,
        top: `${clamp(lens.value.y - LENS_RADIUS - lens.value.lift)}px`,
    };
});

/** The zoomed copy of the wheel, shifted so the pointer location sits at the lens center */
const lensContentStyle = computed(() => {
    if (!lens.value) return {};
    return {
        width: `${size.value * ZOOM}px`,
        height: `${size.value * ZOOM}px`,
        left: `${LENS_RADIUS - lens.value.x * ZOOM}px`,
        top: `${LENS_RADIUS - lens.value.y * ZOOM}px`,
    };
});
</script>

<template>
    <div class="flex flex-col items-center gap-2">
        <div
            ref="fieldEl"
            class="relative w-full max-w-[400px] aspect-square select-none touch-none cursor-crosshair rounded-full outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            role="listbox"
            tabindex="0"
            aria-label="Glaze color wheel. Use arrow keys to move between glazes and Enter to choose."
            :aria-activedescendant="activeDescendant"
            data-testid="color-wheel"
            @pointermove="onPointerMove"
            @pointerdown="onPointerDown"
            @pointerup="onPointerUp"
            @pointercancel="hideLens"
            @pointerleave="onPointerLeave"
            @focus="onFocus"
            @blur="hideLens"
            @keydown="onKeydown"
        >
            <div class="absolute inset-0 rounded-full" :style="{ background: RING_BACKGROUND }" />
            <div
                class="absolute rounded-full border-2 border-white"
                :style="{
                    width: corePct,
                    height: corePct,
                    left: coreOffset,
                    top: coreOffset,
                    background: CORE_BACKGROUND,
                }"
            />

            <span
                v-for="(d, i) in dots"
                :id="optionId(i)"
                :key="d.name"
                role="option"
                :aria-label="d.name"
                :aria-selected="d.name === selectedName ? 'true' : 'false'"
                class="absolute w-2.5 h-2.5 -ml-[5px] -mt-[5px] rounded-full border shadow-[0_0_0_1px_rgba(0,0,0,0.35)] pointer-events-none"
                :class="
                    d.name === selectedName
                        ? 'border-black ring-2 ring-white scale-150 z-10'
                        : 'border-white/90'
                "
                :style="{
                    left: `${d.x * 100}%`,
                    top: `${d.y * 100}%`,
                    backgroundColor: d.hex,
                }"
                :data-glaze="d.name"
            />

            <!-- Magnifying lens -->
            <div
                v-if="lens"
                class="absolute z-20 rounded-full overflow-hidden border-[3px] border-white bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.5),0_8px_24px_rgba(0,0,0,0.35)] pointer-events-none"
                :style="lensStyle"
                data-testid="wheel-lens"
                aria-hidden="true"
            >
                <div class="absolute" :style="lensContentStyle">
                    <div
                        class="absolute inset-0 rounded-full"
                        :style="{ background: RING_BACKGROUND }"
                    />
                    <div
                        class="absolute rounded-full border-4 border-white"
                        :style="{
                            width: corePct,
                            height: corePct,
                            left: coreOffset,
                            top: coreOffset,
                            background: CORE_BACKGROUND,
                        }"
                    />
                    <span
                        v-for="d in dots"
                        :key="d.name"
                        class="absolute rounded-full shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                        :class="
                            d === hoverDot
                                ? 'w-11 h-11 -ml-[22px] -mt-[22px] border-4 border-white ring-[3px] ring-black z-10'
                                : 'w-8 h-8 -ml-4 -mt-4 border-[3px] border-white/90'
                        "
                        :style="{
                            left: `${d.x * size * ZOOM}px`,
                            top: `${d.y * size * ZOOM}px`,
                            backgroundColor: d.hex,
                        }"
                    />
                </div>
                <span
                    v-if="hoverDot"
                    class="absolute left-1/2 bottom-4 -translate-x-1/2 w-max max-w-[176px] text-center px-3 py-1 rounded-lg bg-black/80 text-white text-sm font-semibold leading-tight"
                    data-testid="wheel-lens-label"
                >
                    {{ hoverDot.name }}
                </span>
            </div>
        </div>
        <p class="text-[11px] text-dryline text-center mb-0">
            Hover to magnify, then click a dot to choose a glaze. Lighter glazes sit toward the
            center of the ring, darker toward the edge; neutrals are in the gray center.
        </p>
    </div>
</template>
